import { isSpanContextValid, trace } from "@opentelemetry/api"

/** Read-side Honeycomb UI coordinates for building trace deep-links. */
export interface HoneycombUiConfig {
  /** Honeycomb UI origin, region-aware (e.g. https://ui.eu1.honeycomb.io). */
  uiBaseUrl: string
  /** Honeycomb team slug — the `/<team>/` path segment. */
  team: string
  /** Honeycomb environment slug (e.g. "uat", "production"). */
  environment: string
}

// Org constants: tastytrade's Honeycomb lives on the EU instance under `iggroup`.
const HONEYCOMB_UI_BASE_URL = "https://ui.eu1.honeycomb.io"
const HONEYCOMB_TEAM = "iggroup"

/**
 * Honeycomb UI coordinates for a MODE — `uat` for staging-family, `production`
 * for prod. `uat` is the Honeycomb *environment* name (the deep-link target the
 * gateway routes into); the `deployment.environment` attribute deliberately uses
 * `staging` for the same builds (see `deploymentEnvironmentForMode`).
 */
export function honeycombUiForMode(mode: string): HoneycombUiConfig {
  return {
    uiBaseUrl: HONEYCOMB_UI_BASE_URL,
    team: HONEYCOMB_TEAM,
    environment: mode === "production" ? "production" : "uat",
  }
}

let honeycombUiConfig: HoneycombUiConfig | undefined

/** Stash the deep-link config at telemetry init so the stamper can read it. */
export function setHoneycombUiConfig(
  config: HoneycombUiConfig | undefined
): void {
  honeycombUiConfig = config
}

/**
 * Honeycomb trace permalink, or undefined if no UI config is set. The
 * `/datasets/…` segment is omitted — Honeycomb resolves the trace without it.
 */
export function buildHoneycombTraceUrl(traceId: string): string | undefined {
  if (!honeycombUiConfig) {
    return undefined
  }
  const { uiBaseUrl, team, environment } = honeycombUiConfig
  return `${uiBaseUrl}/${team}/environments/${environment}/trace?trace_id=${traceId}`
}

/** Minimal structural shape of a Sentry event — avoids depending on a Sentry SDK. */
interface SentryEventLike {
  tags?: Record<string, unknown>
  contexts?: Record<string, unknown>
}

/**
 * Stamp a Sentry error with the active Honeycomb trace id + deep-link for
 * error→trace pivoting. No-op when no span is active (e.g. unhandled errors);
 * stamps the tag only (no link) when the UI config is unset.
 */
export function applyHoneycombTraceToSentryEvent<T extends SentryEventLike>(
  event: T
): T {
  const spanContext = trace.getActiveSpan()?.spanContext()
  // Skip invalid contexts (all-zeros NonRecordingSpan id) — they'd link to a dead trace.
  if (!spanContext || !isSpanContextValid(spanContext)) {
    return event
  }
  event.tags = { ...event.tags, honeycomb_trace_id: spanContext.traceId }
  const traceUrl = buildHoneycombTraceUrl(spanContext.traceId)
  if (traceUrl) {
    event.contexts = { ...event.contexts, honeycomb: { trace_url: traceUrl } }
  }
  return event
}
